import { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import projectDataService, { ProjectData } from '../services/projectDataService';

/**
 * Hook to manage project data loading and state
 * Handles both project metadata and game content
 */
export function useProjectManagement(projectId: string | null) {
  const [projectData, setProjectData] = useState<ProjectData | null>(null);
  const [loadingProjectData, setLoadingProjectData] = useState(true);
  const [gameContent, setGameContent] = useState<any>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState('');

  const loadProjectData = async () => {
    if (!projectId) {
      setLoadingProjectData(false);
      return;
    }
    
    setLoadingProjectData(true);
    setAccessDenied(false);
    setAccessDeniedMessage('');
    
    try {
      const data = await projectDataService.getProjectData(projectId);
      setProjectData(data);
      console.log('✅ Loaded project data:', data);
      
      // Also fetch full game data for name, categories, icon, and modal support
      try {
        const gameResponse = await apiService.get(`/games/${projectId}`);
        if (gameResponse.data.success && gameResponse.data.data) {
          const gameData = gameResponse.data.data;
          // Ensure is_editable_in_studio is set correctly (editor is always for studio projects)
          if (gameData.is_editable_in_studio === undefined) {
            gameData.is_editable_in_studio = true;
          }
          // Store the full game object (needed for ProjectEditModal)
          setGameContent(gameData);
          console.log('✅ Loaded game data:', gameData);
        }
      } catch (contentError) {
        console.warn('Could not load game content:', contentError);
      }
    } catch (error: any) {
      console.error('Failed to load project data:', error);
      
      // Check if this is a 403 access denied error
      if (error.response?.status === 403) {
        setAccessDenied(true);
        setAccessDeniedMessage(
          error.response?.data?.error || 
          error.response?.data?.message || 
          'Access denied'
        );
        console.error('🚫 Access denied to project:', projectId);
      }
    } finally {
      setLoadingProjectData(false);
    }
  };

  // Load project data when projectId changes
  useEffect(() => {
    loadProjectData();
  }, [projectId]);

  return { 
    projectData, 
    loadingProjectData, 
    gameContent, 
    loadProjectData,
    accessDenied,
    accessDeniedMessage
  };
}

